import { useQuery } from "@tanstack/react-query";
import { cn, formatKstDateTime } from "@toss-ads/ui";
import type { DownloadLog } from "../lib/types";
import { listAdvertisers } from "../lib/api";

interface Props {
  logs: DownloadLog[];
}

export function DownloadHistoryView({ logs }: Props) {
  const { data: nameById } = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
    select: (rows) => new Map(rows.map((a) => [a.id, a.name])),
  });

  if (logs.length === 0) {
    return (
      <div className="rounded-lg border bg-surface py-12 text-center text-[13px] text-ink-subtle">
        다운로드 기록이 없습니다.
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted">
          <tr>
            <Th width={150}>일시</Th>
            <Th>광고주</Th>
            <Th width={90}>건수</Th>
            <Th width={180}>기간</Th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => {
            const names = log.advertiserIds.map(
              (id) => nameById?.get(id) ?? id.slice(0, 6),
            );
            const joined = names.join(", ");
            return (
              <tr
                key={log.id}
                title={log.fileName}
                className="border-t border-border"
              >
                <Td className="text-ink-subtle">
                  {formatKstDateTime(log.createdAt)}
                </Td>
                <Td className="text-ink" title={joined}>
                  {names.length === 0 ? "—" : joined}
                </Td>
                <Td className="text-ink">{log.leadCount}</Td>
                <Td className="text-ink-subtle">
                  {formatDateRange(log.dateFrom, log.dateTo)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  width,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td
      title={title}
      className={cn("px-2.5 py-3.5 align-middle", className)}
    >
      {children}
    </td>
  );
}

function formatKstDate(iso: string): string {
  const d = new Date(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, "0");
  const day = String(kst.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateRange(from: string | null, to: string | null): string {
  if (!from && !to) return "전체 기간";
  const f = from ? formatKstDate(from) : "";
  const t = to ? formatKstDate(to) : "";
  if (f && t) return `${f} ~ ${t}`;
  return f ? `${f} ~` : `~ ${t}`;
}
